import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Clock, Upload, CheckCircle2, MessageCircle, Loader2, AlertTriangle } from "lucide-react";
import { api, rp } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

const STATUS_UI = {
  none: ["bg-amber-100 text-amber-800", "manualBcaStatusNone"],
  submitted: ["bg-sky-100 text-sky-800", "manualBcaStatusSubmitted"],
  accepted: ["bg-emerald-100 text-emerald-800", "manualBcaStatusAccepted"],
  rejected: ["bg-red-100 text-red-800", "manualBcaStatusRejected"],
  reupload_requested: ["bg-amber-100 text-amber-800", "manualBcaStatusReupload"],
};

const useCountdown = (iso) => {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!iso) { setLeft(null); return; }
    const tick = () => setLeft(Math.max(0, Math.floor((new Date(iso) - new Date()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [iso]);
  return left;
};

export const ManualBca = ({ code }) => {
  const { t } = useI18n();
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const left = useCountdown(v?.hold_expires_at);

  const load = useCallback(() => api.get(`/public/payment/${code}`)
    .then((r) => setV(r.data))
    .catch(() => setV(null)), [code]);
  useEffect(() => {
    load();
  }, [load]);
  if (!v) return null;

  const st = v.proof?.status || "none";
  const [badge, labelKey] = STATUS_UI[st] || STATUS_UI.none;
  const expired = left === 0 && st === "none";
  const mm = left != null ? String(Math.floor(left / 60)).padStart(2, "0") : "--";
  const ss = left != null ? String(left % 60).padStart(2, "0") : "--";

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/public/payment/${code}/proof`, fd);
      setV(r.data);
      toast.success(t("manualBcaUploadSuccess"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("manualBcaUploadFailed"));
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const wa = v.whatsapp
    ? `https://wa.me/${String(v.whatsapp).replace(/\D/g, "")}?text=${encodeURIComponent(v.whatsapp_message || "")}`
    : null;

  return (
    <div className="lm-card rounded-xl p-6" data-testid="manual-bca-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <h2 className="text-base md:text-lg font-semibold">{t("manualBankTransferTitle")}</h2>
        <span data-testid="bca-status-badge" className={`text-[11px] px-2.5 py-1 rounded-full ${badge}`}>
          {t(labelKey)}
        </span>
      </div>

      {st === "none" && (
        <div className={`mt-4 rounded-lg border p-4 flex items-center gap-3 ${expired ? "border-red-300 bg-red-50" : "border-lm-gold/50 bg-lm-gold/5"}`}
          data-testid="bca-countdown">
          {expired ? <AlertTriangle size={18} className="text-red-600" /> : <Clock size={18} className="text-lm-red" />}
          <div className="text-sm">
            {expired ? (
              <span data-testid="bca-hold-expired">
                {t("manualBcaHoldExpired", { minutes: v.hold_minutes })}
              </span>
            ) : (
              <>
                {t("manualBcaHoldPrefix", { minutes: v.hold_minutes })}{" "}
                <b data-testid="bca-timer">{mm}:{ss}</b> {t("manualBcaHoldSuffix")}
              </>
            )}
          </div>
        </div>
      )}

      <div className="mt-5 space-y-2 text-sm">
        <R l="Bank" v={<span data-testid="bca-bank-name">{v.bank?.name}</span>} />
        <R l={t("manualBcaAccountNumber")} v={
          <span className="inline-flex items-center gap-2">
            <span data-testid="bca-account-number" className="font-semibold tracking-wide">{v.bank?.account_number}</span>
            <button data-testid="bca-copy-number" onClick={() => {
              navigator.clipboard?.writeText(v.bank?.account_number || "");
              toast.success(t("manualBcaCopySuccess"));
            }} className="text-xs px-2 py-1 rounded border border-black/15 hover:border-lm-red">
              {t("manualBcaCopy")}
            </button>
          </span>} />
        <R l={t("manualBcaAccountHolder")} v={<span data-testid="bca-account-holder">{v.bank?.account_holder}</span>} />
        <div className="h-px bg-black/10 my-2" />
        <R l={t("manualBcaRental")} v={rp(v.final_rental_total || 0)} />
        <R l={t("manualBcaRefundableDeposit")} v={rp(v.deposit_amount || 0)} />
        <R l={t("manualBcaTotalTransfer")} v={<span className="font-display text-xl text-lm-red" data-testid="bca-amount">{rp(v.total_payment || 0)}</span>} />
        {v.refund_details_on_file && (
          <R l={t("manualBcaDepositRefundTo")} v={<span data-testid="bca-refund-masked">{v.refund_bank_name} · {v.refund_account_number_masked}</span>} />
        )}
      </div>

      {v.proof?.reject_reason && (
        <p className="mt-4 text-xs text-red-700" data-testid="bca-reject-reason">
          {t("manualBcaTeamNote")} {v.proof.reject_reason}
        </p>
      )}

      {v.hub?.name && (
        <div className="mt-4 rounded-lg border border-black/10 bg-cream-dark/40 p-4 text-sm" data-testid="bca-hub-contact">
          <div className="text-[11px] uppercase tracking-widest text-ink-soft">
            {t("manualBcaYourHub")}
          </div>
          <div className="mt-1 font-medium" data-testid="bca-hub-name">{v.hub.name}</div>
          {v.hub.address
            ? <div className="text-xs text-ink-soft mt-0.5" data-testid="bca-hub-address">{v.hub.address}</div>
            : <div className="text-xs text-lm-red mt-0.5" data-testid="bca-hub-address-missing">
                {t("manualBcaHubMissing")}
              </div>}
          {v.hub.maps_url && (
            <a href={v.hub.maps_url} target="_blank" rel="noreferrer" data-testid="bca-hub-maps"
              className="mt-1.5 inline-block text-xs text-lm-red hover:underline">Google Maps →</a>
          )}
        </div>
      )}

      {st === "accepted" ? (
        <div className="mt-6 flex items-center gap-2 text-sm text-emerald-700" data-testid="bca-verified">
          <CheckCircle2 size={16} /> {t("manualBcaVerified")}
        </div>
      ) : (
        <div className="mt-6">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,application/pdf"
            data-testid="bca-proof-input" className="hidden"
            onChange={(e) => upload(e.target.files?.[0])} />
          <button
            data-testid="bca-upload-btn"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            className={st === "submitted"
              ? [
                "inline-flex items-center gap-2 rounded-full border-2 border-lm-red bg-white",
                "px-7 py-3.5 text-sm font-semibold text-lm-red transition-colors",
                "hover:bg-lm-red/5 focus-visible:outline-none focus-visible:ring-2",
                "focus-visible:ring-lm-red focus-visible:ring-offset-2 disabled:opacity-40",
              ].join(" ")
              : [
                "lm-btn-primary inline-flex items-center gap-2 rounded-full px-7 py-3.5",
                "text-sm font-semibold focus-visible:outline-none focus-visible:ring-2",
                "focus-visible:ring-lm-red focus-visible:ring-offset-2 disabled:opacity-40",
              ].join(" ")}
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
            {st === "submitted" ? t("manualBcaReplaceProof") : t("manualBcaUploadProof")}
          </button>
          <p className="mt-2 text-xs text-ink-soft">{t("manualBcaFileTypes")}</p>
        </div>
      )}

      {wa && (
        <div className="mt-6" data-testid="bca-whatsapp-confirmation-section">
          <div
            data-testid="bca-whatsapp-confirmation-callout"
            className="border border-lm-red/30 bg-lm-red/5 p-4"
          >
            <p
              data-testid="bca-whatsapp-confirmation-title"
              className="text-sm font-semibold text-lm-red"
            >
              {t("whatsappPaymentConfirmationTitle")}
            </p>
            <p
              data-testid="bca-whatsapp-confirmation-helper"
              className="mt-1 text-sm text-ink-soft"
            >
              {t("whatsappPaymentConfirmationHelper")}
            </p>
          </div>
          <a
            href={wa}
            target="_blank"
            rel="noreferrer"
            data-testid="bca-wa-confirm"
            className={[
              "lm-btn-primary mt-3 inline-flex min-h-12 w-full items-center justify-center gap-2",
              "rounded-full px-7 py-3.5 text-sm font-semibold sm:w-auto",
            ].join(" ")}
          >
            <MessageCircle size={17} />
            {t("whatsappPaymentConfirmationCta")}
          </a>
        </div>
      )}

      <p className="mt-5 text-xs text-ink-soft" data-testid="bca-note">
        {t("manualBcaDashboardNote")}
      </p>
    </div>
  );
};

const R = ({ l, v }) => (
  <div className="flex justify-between gap-4"><span className="text-ink-soft">{l}</span><span className="text-right">{v}</span></div>
);
